<template>
  <section :class="b({ disabled })">
    <div :class="b('calculation')">
      <div
        v-if="!disabled"
        :class="b('tool-bar')"
      >
        <el-select
          v-for="(item, key) in toolBarData"
          :key="key"
          v-model="item.value"
          :select-dropdown-option="{
            minWidth: 180
          }"
          @input="handleFieldSelect($event, key)"
        >
          <template #ref="{ visible }">
            <div :class="b('toolbar-popover-reference', { active: visible })">
              <span>
                {{ item.name }}
              </span>
              <em :class="visible ? 'el-icon-caret-top' : 'el-icon-caret-bottom'" />
            </div>
          </template>
          <el-option
            v-for="option of item.option"
            :key="option.value"
            :label="option.label"
            :value="option.value"
            :disabled="isDisabledEstimateLabourOption(option) || isDisabledActualLabourOption(option) || isDisabledPointOption(option)"
          >
            <template #after>
              <span
                v-if="isDisabledEstimateLabourOption(option)"
                :class="b('option-tag')"
              >
                {{ $t('common.notEnabled') }}
              </span>
              <span
                v-if="isDisabledActualLabourOption(option)"
                :class="b('option-tag')"
              >
                {{ $t('common.notEnabled') }}
              </span>
              <span
                v-if="isDisabledPointOption(option)"
                :class="b('option-tag')"
              >
                {{ $t('common.notEnabled') }}
              </span>
            </template>
          </el-option>
        </el-select>

        <!-- 插入自定义函数 -->
        <el-popover
          v-model="customPopover"
          trigger="click"
          size="mini"
          placement="bottom"
          :visible-arrow="false"
          :popper-class="b('custom-popover-warp')"
        >
          <div
            slot="reference"
            :class="b('toolbar-popover-reference', { active: customPopover })"
          >
            <span>
              {{ $t('project.overview.formula.addFunction') }}
            </span>
            <em :class="customPopover ? 'el-icon-caret-top' : 'el-icon-caret-bottom'" />
          </div>

          <FormulaCustomPopover
            :param-list="paramList"
            :project-id="projectId"
            @cancel="customPopover = false"
            @submit="handleCustomSubmit"
          />
        </el-popover>

        <!-- 预设函数 -->
        <el-dropdown
          size="default"
          trigger="click"
        >
          <span
            :class="[b('preset-func-label'), 'el-dropdown-link']"
          >
            {{ $t('project.overview.formula.preset') }}
          </span>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item
              v-for="option in functionList"
              :key="option.value"
              @click.native="handleFunctionSelect(option)"
            >
              {{ option.label }}
            </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>

      <div :class="b('edit-warp')">
        <div
          ref="editorContent"
          :class="b('edit')"
          contenteditable
          @keyup="updateEditRange"
          @click="updateEditRange"
          @input="handleInput"
          @paste="handlePaste"
          @keyup.right="handleKeyUpRight"
          @keypress="handleKeyPress"
          v-html="expressionVo"
        />

        <div
          v-if="!value"
          :class="b('edit-placeholder')"
        >
          <div>
            {{ $t('project.overview.expressPlaceholder1') }}
            <span :class="b('operator')">+</span>
            <span :class="b('operator')">-</span>
            <span :class="b('operator')">*</span>
            <span :class="b('operator')">/</span>
            <template v-if="!isOverseasVersion">
              {{ $t('project.overview.expressPlaceholder2') }}
            </template>
          </div>
          <div>{{ $t('project.overview.expressPlaceholder3') }}</div>
        </div>
      </div>
    </div>

    <div
      v-if="!disabled"
      :class="b('syntax-check')"
    >
      <el-button
        size="small"
        :loading="checkExpressionEvent.loading"
        @click="checkExpressionEvent.fn"
      >
        {{ $t('project.issueType.customFieldDialog.form.formula.checkSyntax') }}
      </el-button>

      <template v-if="formulaCheckInfo">
        <span :class="b('syntax-check-info', [formulaCheckInfo.isLegal ? 'success': 'error'])">
          {{ formulaCheckInfo.isLegal ? $t('project.issueType.customFieldDialog.form.formula.checkInit') : formulaCheckInfo.reason }}
        </span>
      </template>
    </div>
  </section>
</template>

<script>
import { formulaCheckExpression } from 'common-module/src/api/field';
import theme from '@zk/theme/lib/variables.scss';
import _ from 'lodash';
import { createLoadingEvent } from '@zk/framework-vue';
import FormulaCustomPopover from './FormulaCustomPopover';
import { isOverseasVersion } from 'common-module/src/util';

const TYPE_TAG = 'tag';
const TYPE_TEXT = 'txt';

export default {
  name: 'FormulaForm',
  components: { FormulaCustomPopover },
  inject: ['fieldConfig'],
  props: {
    options: {
      type: Object,
      default: () => ({})
    },
    paramList: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    projectId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      bemNS: theme.bemNS,
      formulaCheckInfo: null,
      editRange: null,
      checkExpressionEvent: createLoadingEvent(this.handleCheckExpress),
      expressionVo: null,
      formulaCustomVisible: false,
      customPopover: false,
      customFunctionList: [],
      isOverseasVersion
    };
  },
  computed: {
    toolBarData() {
      const name = {
        customFieldVOList: this.$t('project.issueType.customFieldDialog.form.formula.insertCalcField'),
        unitVoList: this.$t('project.issueType.customFieldDialog.form.formula.insertUnits')
      };
      const keys = Object.keys(this.$props.options).filter(key => !['functionList', 'operatorVoList'].includes(key));
      return keys.reduce((prev, key) => {
        prev[key] = {
          name: name[key],
          value: null,
          option: this.transformOption(this.$props.options[key], 'key', 'value')
        };
        return prev;
      }, {});
    },
    functionList() {
      const { functionList:list } = this.$props.options || {};
      if (!list) {
        return [];
      }
      return list.filter(l => l.name !== 'customize').map(item => {
        return {
          functionType: item.name,
          field: item.fieldCode,
          object: item.object,
          conditions: item.conditionCode ? { [item.conditionCode]: item.conditionValue } : null,
          label: item.desc,
          value: this.getFuncValue(item),
          key: this.getFuncLabel(item)
        };
      });
    },
    findLabelAll() {
      const { customFieldVOList, unitVoList } = this.toolBarData;
      return _.concat(customFieldVOList?.option, unitVoList?.option);
    }
  },
  watch: {
    options(value) {
      this.$nextTick(
        () => {
          this.replaceName(value);
          this.handleInput();
        }
      );
    },
    value: {
      immediate: true,
      handler(value){
        if (!this.$refs.editorContent){
          this.$nextTick(() => this.setEditValue(value));
        } else {
          this.setEditValue(value);
        }
      }
    }
  },
  methods: {
    isDisabledEstimateLabourOption(option) {
      return this.isDisabledOption(option, 'estimateLabour');
    },
    isDisabledActualLabourOption(option) {
      return ['actualLabour', 'remainingLabour'].includes(option.value) && !this.isShowField('labourRecord');
    },
    isDisabledPointOption(option) {
      return this.isDisabledOption(option, 'estimatePoint');
    },
    isShowField(code) {
      const field = this.fieldConfig?.usedFields?.find(field => field.fieldCode === code);
      if (!field) {
        return false;
      }
      return isOverseasVersion || field.showed === 1;
    },
    isDisabledOption(option, code) {
      return option.value === code && !this.isShowField(code);
    },
    setEditValue(value) {
      if (this.$refs.editorContent.innerHTML !== value) {
        // this.$refs.editorContent.innerHTML = value;
        this.expressionVo = value;
      }
    },
    replaceName() {
      if (!this.value){
        return;
      }
      const filedNodes = this.$refs.editorContent.querySelectorAll('[data-value]');
      if (!filedNodes?.length) {
        return [];
      }
      filedNodes.forEach(
        node => {
          const text = node.innerText;
          const value = node.getAttribute('data-value');
          const label = this.findLabelAll.find(item => item.value === value)?.label;
          if (!label){
            return;
          }
          const reg = value.includes('(') ? /(\().*?(\))/ : /(\s+).*(\s+)/ ;
          node.innerText = text.replace(reg, `$1${label}$1`);
        }
      );
    },
    updateEditRange() {
      this.editRange = window.getSelection().getRangeAt(0);
    },
    handleInput() {
      this.$emit('input', this.$refs.editorContent.innerHTML);
    },
    handlePaste(event) {
      let paste = (event.clipboardData || window.clipboardData).getData('text');
      paste = paste.replace(/[\r\n]/g, '');
      const selection = window.getSelection();
      if (!selection.rangeCount) {
        return false;
      }
      selection.deleteFromDocument();
      selection.getRangeAt(0).insertNode(document.createTextNode(paste));
      event.preventDefault();
      this.handleInput();
    },
    handleKeyUpRight() {
      const range = window.getSelection().getRangeAt(0);
      if (range.startContainer?.parentElement?.classList.contains(this.b('express-tag'))){
        range.selectNode(range.startContainer.parentElement.previousElementSibling);
        range.collapse(false);
      }
    },
    handleFieldSelect(val, key) {
      const { value, label } = _.find(this.toolBarData[key]?.option, { value: val }) || {};
      const type = key === 'operatorVoList' ? TYPE_TEXT : TYPE_TAG;
      if (!value) {
        return;
      }
      this.insertTag({ label, value, key }, type);
      this.handleInput();
      this.$nextTick(() => {
        this.$set(this.toolBarData[key], 'value', null);
      });
    },
    handleFunctionSelect(option) {
      const { value, key } = option || {};
      if (!value) {
        return;
      }
      this.insertTag({ label: key, value });
      this.handleInput();
    },
    handleCustomSubmit(value) {
      const conditionCode = (value.conditions && Object.keys(value.conditions)[0]) || '';
      const item = {
        ...value,
        name: value.functionType,
        fieldCode: (value.field === 'count' ? '' : value.field) || '',
        fieldName: value.fieldName || '',
        conditionCode: conditionCode === 'null' ? '' : conditionCode,
        conditionValue: (value.conditions && Object.values(value.conditions)[0]) || ''
      };

      const label = this.getFuncLabel(item);
      const key = this.getFuncValue(item);
      this.customFunctionList.push({
        ...item,
        key: label,
        value: key,
        conditions: item.conditionCode ? item.conditions : null
      });
      this.insertTag({ label, value: key });
      this.handleInput();

      this.customPopover = false;
    },
    insertTag({ label, value, key }, type = TYPE_TAG) {
      const node = type === TYPE_TAG
        ? this.createTagNode({ label, value, key })
        : document.createTextNode(value);

      const range = this.insertNode(node);

      setTimeout(() => {
        this.collapseToRangeEnd(range);
      }, 0);
    },
    insertNode(node) {
      let range = this.editRange;
      if (!range) {
        range = document.createRange();
        range.selectNodeContents(this.$refs.editorContent);
        range.collapse(false);
      }
      range.insertNode(document.createTextNode(' '));
      range.insertNode(node);
      return range;
    },
    createTagNode({ label, value, key }) {
      const node = document.createElement('span');
      const classKey = key ? key.toLowerCase() : '';
      node.setAttribute('contenteditable', false);
      node.setAttribute('class', this.b('express-tag', classKey));
      node.setAttribute('data-value', value);
      node.innerHTML = `&nbsp;&nbsp;${label}&nbsp;&nbsp;`;
      node.onclick = this.handleExpressTagClick;
      return node;
    },
    collapseToRangeEnd(range) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      selection.collapseToEnd();
      this.updateEditRange();
    },
    handleExpressTagClick(e) {
      const target = e.target;
      const range = window.getSelection().getRangeAt(0);
      range.selectNode(target);
      range.collapse(false);
      this.collapseToRangeEnd(range);
    },
    getAnalyzeFormula() {
      const childNodes = this.$refs.editorContent.childNodes;
      let formatValue = '';
      childNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const value = node.getAttribute('data-value');
          formatValue += value;
        }
        if (node.nodeType === 3) {
          formatValue += node.data.replace(/\s+/g, '');
        }
      });
      return formatValue;
    },
    getUsedFields() {
      const childNodes = this.$refs.editorContent?.childNodes || [];
      return Array.from(childNodes).filter(node => node.nodeType === 1).map((node) => node.getAttribute('data-value'));
    },
    getAnalyzeFields() {
      const filedNodes = this.$refs.editorContent.querySelectorAll('[data-value]');
      if (!filedNodes?.length) {
        return [];
      }
      const values = Array.from(filedNodes).map(
        (item) => item.getAttribute('data-value')
      );
      const list = [...this.functionList, ...this.customFunctionList];
      return list.filter((item) => values.includes(item.value))
        .map(({ functionType, field, object, value, conditions }) => ({
          expression: value,
          functionType,
          field: field === 'count' ? '' : field,
          object,
          conditions
        }));
    },
    async handleCheckExpress() {
      const expression = this.getAnalyzeFormula();
      const functionModules = this.getAnalyzeFields();
      await this.checkExpress(expression, functionModules);
    },
    handleKeyPress(e) {
      if (e.code === 'Enter') {
        document.execCommand('insertHTML', false, '');
        e.preventDefault();
      }
    },
    // 校验公式语法
    async checkExpress(expression, functionModules) {
      if (!expression) {
        return;
      }
      const res = await formulaCheckExpression({ expression, functionModules });
      this.formulaCheckInfo = res;
      return res ? res.isLegal : false;
    },
    transformOption(option, valueName, labelName) {
      if (!option) {
        return [];
      }
      return option.map(item => ({
        value: item[valueName],
        label: item[labelName]
      }));
    },
    getFuncLabel(item) {
      const fieldName = item.fieldName && `-${item.fieldName}`;
      const condition = item.conditionCode ? `${item.conditionName}="${item.conditionValueName}"` : '';
      return `${item.name}(${item.objectName}${fieldName};${condition})`;
    },
    getFuncValue(item) {
      const fieldCode = item.fieldCode && `.${item.fieldCode}`;
      const condition = item.conditionCode ? `${item.conditionCode}=${item.conditionValue}` : '';
      return `${item.name}(${item.object}${fieldCode};${condition})`;
    },

    // 输入限制
    // inputLimits(event) {
    //   this.textarea = event.replace(/[^+\-*/.()（）\s\da-zA-Z\u2E80-\u9FFF]+/g,'');
    // },
    clear() {
      this.editRange = null;
      this.formulaCheckInfo = null;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@zk/theme/lib/mixins.scss';

@include b(formula-form) {
  width: 100%;
  overflow: hidden;
  @include m(disabled) {
    pointer-events: none;
    background: #fafafa;
  }

  @include e(calculation) {
    border: 1px solid var(--color-border-1);
    border-radius: var(--radius-mini);
  }
  @include e(tool-bar) {
    padding: 0px 10px;
    border-bottom: 1px solid #e0e0e0;
    line-height: 36px;
    .el-select {
      min-width: auto;
    }
  }
  @include e(toolbar-drop) {
    margin-right: var(--m-extra);

    ::v-deep .zk-confirm-dropdown__reference{
      &,[class*=el-icon-]{
        color: var(--font-color-1);
        font-weight: var(--weight-font-Semiblod);
      }
      [class*=el-icon-]{
        color: var(--font-color-3);
        font-size: var(--size-font-9);
        margin-left: 6px;
      }

      &.zk-confirm-dropdown__reference--active{
        &,[class*=el-icon-]{
          color: var(--primary-color);
        }
        font-weight: var(--weight-font-Blod);
        background: #FAFAFA;
      }

      .zk-confirm-dropdown__reference-text{
        width: auto;
        padding: var(--p-lr-mini);
        cursor: pointer;
      }
    }
  }
  @include e(toolbar-popover-reference) {
    padding: var(--p-lr-mini);
    cursor: pointer;
    font-size: var(--size-font-9);
    border: 1px solid var(--color-border-1);
    line-height: 24px;
    width: auto;
    border-radius: var(--radius-mini);
    display: inline-block;
    margin-right: 10px;

    [class*=el-icon-]{
      color: var(--font-color-3);
      font-size: var(--size-font-9);
      margin-left: 6px;
    }
    @include m(active) {
      &,[class*=el-icon-] {
        color: var(--primary-color);
      }
      font-weight: var(--weight-font-Blod);
      background: #FAFAFA;
    }
  }
  @include e(edit-warp) {
    position: relative;
    width: 100%;
    height: 100px;
    padding: 10px 12px;
  }
  @include e(edit-placeholder) {
    position: absolute;
    top: 0;
    left: 0;
    line-height: 1.5;
    padding: 10px 12px;
    font-size: var(--size-font-7);
    color: var(--font-color-3);
    pointer-events: none;
  }

  @include e(edit) {
    height: 100%;
    line-height: 28px;
    white-space: pre-wrap;
    overflow: auto;
  }

  @include e(syntax-check){
    display: flex;
    align-items: center;
    margin-top: 10px;

    .el-button{
      border-color: var(--primary-color);
      color: var(--primary-color);
      min-width: 80px;
    }
  }
  @include e(syntax-check-info) {
    font-size: var(--size-font-9);
    margin-left: 10px;
    line-height: 1.2;

    @include m(error){ color: #F5222D; }
    @include m(success){ color:#67C23A; }
  }
  @include e(preset-func-label) {
    font-size: var(--size-font-9);
    line-height: 1.2;
    color: var(--primary-color);
    cursor: pointer;
    font-weight: var(--weight-font-Blod);
  }
  @include e(operator){
    display: inline-block;
    width: 20px;
    height: 20px;
    color: var(--font-color-1);
    text-align: center;
    line-height: 20px;
    margin-left: 10px;
    font-size: var(--size-font-7);
    font-weight: var(--weight-font-Blod);
    background: #F3F3F3;
    border-radius: 2px;
    &:last-child{
      margin-right: 10px;
    }
  }
}
</style>
<style lang="scss">
@include b(formula-form) {
  .el-select {
    width: unset !important;
  }
  @include e(option-tag) {
    height: 18px;
    display: inline-block;
    padding: 0 4px;
    color: #4c52a8;
    border-radius: 13px;
    background-color: rgba(66,91,120,.1);
    font-size: 12px;
    white-space: nowrap;
    line-height: 18px;
    text-align: center;
    font-weight: 400!important;
    margin-left: 4px;
  }
  @include e(express-tag) {
    height: 28px;
    line-height: 36px;
    margin-left: 4px;
    margin-right: var(--icon-mr-smaller);
    padding: 4px 0;
    border-radius: var(--radius-mini);
    background: #ECF3FF;
    font-size: var(--size-font-7);
    font-weight: var(--weight-font-Blod);
    color: var(--font-color-1);

    @include m(customfieldvolist) {
      background: #FFF7E7;
    }
    @include m(unitvolist) {
      background: #F6F2FF;
    }
  }
  @include e(custom-popover-warp) {
    // margin-left: -8px;
    padding: 0 0 10px 0;
  }
}
</style>
